import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'

import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import MarkdownRender from 'LuxKit/MarkdownRender'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Image from 'components/Common/Image'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import OfferBadgeLabel from 'components/Common/OfferBadgeLabel/OfferBadgeLabel'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import OfferDescription from 'components/OfferPage/OfferDescription'
import OfferPromotions from 'components/OfferPage/OfferPromotions'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { TasteOfGoldPromoCard } from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoStyles'
import TasteOfGoldPromoContent from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldPromoContent'

import PropertyPopularFacilities from './PropertyPopularFacilities'

const OfferIntroSection = styled(BodyText)`
  grid-row: 4;

  ${mediaQueryUp.tablet} {
    grid-row: 3;
    grid-column: 1;
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-row: 2;
    grid-column: span 2;
  }

  ${mediaQueryUp.desktop} {
    grid-column: 1;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.desktop} {
    margin-left: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

interface Props {
  property: App.Property.Property
  /**
   * @deprecated Use `property` instead. (SPP combines multiple offers into a single property; there
   * is no canonical offer.)
   */
  offer: App.HotelOffer
  promotions: Array<App.OfferPackageOptionView>
  showTasteOfGoldPromo?: boolean
}

function PropertyPropertyDetails(props: Props) {
  const {
    property,
    offer,
    promotions,
    showTasteOfGoldPromo,
  } = props

  const location = property.location.title

  return (<>
    <HeaderSection>
      <Header>
        <VerticalSpacer gap={8}>
          <BodyTextBlock
            variant="small"
            data-testid="offer-location"
            startIcon={<LineMapMarkerIcon />}
          >
            {location && `${location} · `}<TextLink href="#getting-there">View on map</TextLink>
          </BodyTextBlock>
          <Heading variant="heading2" as="h1">{property.name}</Heading>
          <BodyText variant="large" as="h2">{property.headline}</BodyText>

          {isOfferRatingDisplayable(property.rating) && <OfferRating
            link
            inlineLabel
            variant="small"
            rating={property.rating}
          />}
        </VerticalSpacer>
        {property.logo?.id &&
          <PropertyLogo
            dpr={2}
            height={75}
            enhancedFormats={false}
            id={property.logo.id}
            alt={`${property.name || 'Vendor'} logo`}
          />}
      </Header>
      {showTasteOfGoldPromo && <TasteOfGoldPromoCard>
        <CSSBreakpoint max="mobile">
          <TasteOfGoldPromoContent layout="vertical" size="medium" context="offerPage" />
        </CSSBreakpoint>
        <CSSBreakpoint min="tablet">
          <TasteOfGoldPromoContent layout="horizontal" size="medium" context="offerPage" />
        </CSSBreakpoint>
      </TasteOfGoldPromoCard>}
    </HeaderSection>
    {offer.badge && <OfferBadgeLabel badge={offer.badge} />}

    <OfferIntroSection variant="large">
      {property.description && <OfferDescription description={property.description} />}
      {property.additionalDescription && <MarkdownRender type="compact" fontSize="medium" content={property.additionalDescription} />}
      {property.popularFacilities.length > 0 && <PropertyPopularFacilities popularFacilities={property.popularFacilities} />}
      {promotions.length > 0 && <OfferPromotions offer={offer} promotions={promotions} />}
    </OfferIntroSection>
  </>)
}

export default React.memo(PropertyPropertyDetails)
